import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LifeBuoy, Phone } from 'lucide-react';
import {
  COMPLAINT_REASON_LABELS,
  COMPLAINT_STATUS_LABELS,
  ComplaintStatus,
  type ComplaintReason,
} from '@kick/types';
import { api, getErrorMessage } from '@/lib/api';
import { usePuede } from '@/lib/permisos';
import { cn, formatCOP } from '@/lib/cn';
import { Boton, Cargando, ErrorCarga, Vacio } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

interface Reclamo {
  id: string;
  orderId: string;
  code: string;
  total: number;
  reason: ComplaintReason;
  detail: string | null;
  status: ComplaintStatus;
  resolution: string | null;
  createdAt: string;
  resolvedAt: string | null;
  customerName: string;
  customerPhone: string;
  merchantName: string;
}

const COLORES: Record<ComplaintStatus, string> = {
  [ComplaintStatus.OPEN]: 'bg-red-100 text-red-800',
  [ComplaintStatus.IN_REVIEW]: 'bg-amber-100 text-amber-800',
  [ComplaintStatus.RESOLVED]: 'bg-emerald-100 text-emerald-800',
  [ComplaintStatus.REJECTED]: 'bg-kick-gray/15 text-kick-gray',
};

/**
 * Bandeja de soporte.
 *
 * Por defecto solo lo que sigue sin resolver, que es lo único sobre lo que hay
 * que hacer algo. El histórico se consulta cuando alguien pregunta por un caso
 * concreto, no cada mañana.
 */
export function Reclamos() {
  const puede = usePuede();
  const cliente = useQueryClient();
  const avisos = useAvisos();
  const [todos, setTodos] = useState(false);

  const lista = useQuery({
    queryKey: ['reclamos-admin', todos],
    queryFn: async () =>
      (await api.get<Reclamo[]>(`/admin/reclamos?limit=100${todos ? '&todos=true' : ''}`)).data,
  });

  const resolver = useMutation({
    mutationFn: async (datos: { id: string; status: ComplaintStatus; resolution?: string }) =>
      (await api.patch(`/admin/reclamos/${datos.id}`, datos)).data,
    onSuccess: () => {
      avisos.exito('Reclamo actualizado');
      void cliente.invalidateQueries({ queryKey: ['reclamos-admin'] });
    },
    onError: (e) => avisos.error(getErrorMessage(e)),
  });

  if (lista.isLoading) return <Cargando />;
  if (lista.isError) {
    return (
      <ErrorCarga mensaje={getErrorMessage(lista.error)} onReintentar={() => void lista.refetch()} />
    );
  }

  const reclamos = lista.data ?? [];

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">Reclamos</h1>
          <p className="mt-1 text-sm text-kick-gray">
            Lo que los clientes reportaron sobre sus pedidos.
          </p>
        </div>
        <button
          onClick={() => setTodos((v) => !v)}
          aria-pressed={todos}
          className={cn(
            'rounded-kick border px-3 py-1.5 text-sm font-semibold transition',
            todos
              ? 'border-kick-red bg-kick-red text-white'
              : 'border-kick-gray/25 text-kick-ink hover:border-kick-gray/50',
          )}
        >
          Ver también los cerrados
        </button>
      </div>

      {reclamos.length === 0 ? (
        <div className="mt-6">
          <Vacio
            icono={<LifeBuoy className="h-6 w-6" />}
            titulo={todos ? 'Ningún reclamo' : 'Nada pendiente'}
            descripcion={
              todos
                ? 'Nadie ha reportado problemas con sus pedidos.'
                : 'Todos los reclamos están cerrados. Buen día.'
            }
          />
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {reclamos.map((r) => (
            <Ficha
              key={r.id}
              reclamo={r}
              soloLectura={!puede('reclamos', 'resolver')}
              guardando={resolver.isPending}
              onResolver={(status, resolution) => resolver.mutate({ id: r.id, status, resolution })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Ficha({
  reclamo: r,
  soloLectura,
  guardando,
  onResolver,
}: {
  reclamo: Reclamo;
  /** Puede verlo pero no resolverlo: se esconden los botones. */
  soloLectura: boolean;
  guardando: boolean;
  onResolver: (status: ComplaintStatus, resolution?: string) => void;
}) {
  const [respuesta, setRespuesta] = useState('');
  const abierto = r.status === ComplaintStatus.OPEN || r.status === ComplaintStatus.IN_REVIEW;

  return (
    <section className="card-kick p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-kick-dark">{COMPLAINT_REASON_LABELS[r.reason]}</p>
          <p className="text-sm text-kick-gray">
            {r.code} · {r.merchantName} · {formatCOP(r.total)}
          </p>
        </div>
        <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-bold', COLORES[r.status])}>
          {COMPLAINT_STATUS_LABELS[r.status]}
        </span>
      </div>

      {r.detail && (
        <p className="mt-3 rounded-kick bg-kick-light px-3 py-2 text-sm text-kick-ink">{r.detail}</p>
      )}

      {/* El teléfono a la vista: casi todo se resuelve llamando. */}
      <p className="mt-3 flex items-center gap-2 text-sm text-kick-gray">
        <Phone className="h-4 w-4" />
        {r.customerName} · <a href={`tel:${r.customerPhone}`} className="underline">{r.customerPhone}</a>
      </p>

      {r.resolution && (
        <div className="mt-3 rounded-kick border-l-4 border-kick-red bg-kick-light px-3 py-2">
          <p className="text-xs font-bold uppercase tracking-wide text-kick-gray">Resolución</p>
          <p className="mt-0.5 text-sm text-kick-ink">{r.resolution}</p>
        </div>
      )}

      {abierto && (
        <div className="mt-4 space-y-3 border-t border-kick-gray/15 pt-4">
          <textarea
            value={respuesta}
            onChange={(e) => setRespuesta(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder="Se le devolvió el valor del producto faltante y se habló con el comercio."
            aria-label="Resolución del reclamo"
            className="w-full rounded-kick border border-kick-gray/30 px-3 py-2 text-sm text-kick-ink outline-none transition focus:border-kick-red"
          />
          <p className="text-xs text-kick-gray">
            El cliente recibe este texto tal cual, por la campana y por correo.
          </p>

          {soloLectura ? (
            <p className="text-xs text-kick-gray">Tu cuenta puede ver los reclamos, pero no resolverlos.</p>
          ) : (
          <div className="flex flex-wrap gap-2">
            {r.status === ComplaintStatus.OPEN && (
              <Boton
                variante="secundario"
                className="px-3 py-1.5 text-xs"
                onClick={() => onResolver(ComplaintStatus.IN_REVIEW)}
                cargando={guardando}
              >
                Tomarlo
              </Boton>
            )}
            <Boton
              className="px-3 py-1.5 text-xs"
              onClick={() => onResolver(ComplaintStatus.RESOLVED, respuesta.trim())}
              disabled={respuesta.trim().length < 5}
              cargando={guardando}
            >
              Resolver
            </Boton>
            <Boton
              variante="peligro"
              className="px-3 py-1.5 text-xs"
              onClick={() => onResolver(ComplaintStatus.REJECTED, respuesta.trim())}
              disabled={respuesta.trim().length < 5}
            >
              Sin lugar
            </Boton>
          </div>
          )}
        </div>
      )}
    </section>
  );
}
